/**
 * One share-button behaviour for every "Share" control on the site.
 *
 * Before this existed, the two native-share buttons each handled the share
 * sheet's rejection differently, and both got one case wrong:
 *
 * - The public fund page (MissionHeader) treated EVERY rejection as "the sheet
 *   refused" and fell through to the clipboard. But the overwhelmingly common
 *   rejection is the visitor simply closing the sheet (`AbortError`). On iOS
 *   Safari the clipboard write that follows runs after an `await`, outside the
 *   click's user-activation window, so it throws — and a supporter who just
 *   changed their mind was told "Could not copy the link". On browsers where
 *   the write does succeed, closing the sheet silently overwrote whatever they
 *   had on their clipboard.
 * - The creator's own page (MyPage) swallowed EVERY rejection, so a genuine
 *   failure (`NotAllowedError`, a sheet that cannot open) dead-ended with no
 *   copy and no message.
 *
 * The rule now: a dismissed sheet is a decision, not an error — do nothing.
 * Any other failure falls through to the clipboard so the click never
 * dead-ends, and the caller reports the real outcome.
 */

export type ShareOutcome = 'shared' | 'dismissed' | 'copied' | 'failed';

export interface ShareEnv {
  share?: (data: { title?: string; text?: string; url?: string }) => Promise<void>;
  writeText?: (text: string) => Promise<void>;
}

export interface ShareRequest {
  /** What the native share sheet receives. Omit to go straight to the clipboard. */
  shareData?: { title?: string; text?: string; url: string };
  /** What goes on the clipboard when there is no sheet, or it failed. */
  copyText: string;
}

/** A rejection that means "the person closed the sheet", per the Web Share spec. */
export function isShareDismissal(err: unknown): boolean {
  return (
    typeof err === 'object' &&
    err !== null &&
    (err as { name?: unknown }).name === 'AbortError'
  );
}

export function browserShareEnv(): ShareEnv {
  if (typeof navigator === 'undefined') return {};
  const nav = navigator as Navigator & {
    share?: ShareEnv['share'];
  };
  return {
    share: typeof nav.share === 'function' ? nav.share.bind(nav) : undefined,
    writeText:
      nav.clipboard && typeof nav.clipboard.writeText === 'function'
        ? nav.clipboard.writeText.bind(nav.clipboard)
        : undefined,
  };
}

export async function shareOrCopy(
  req: ShareRequest,
  env: ShareEnv = browserShareEnv()
): Promise<ShareOutcome> {
  if (req.shareData && env.share) {
    try {
      await env.share(req.shareData);
      return 'shared';
    } catch (err) {
      if (isShareDismissal(err)) return 'dismissed';
      // Anything else: the sheet could not do its job. Fall through.
    }
  }
  if (!env.writeText) return 'failed';
  try {
    await env.writeText(req.copyText);
    return 'copied';
  } catch {
    return 'failed';
  }
}
